import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TrueOrFalseActivity,
  TrueOrFalseActivityProgress,
} from '@/models/_deprecated/ActivityProgress';
import { ElementPreviewFactoryProps } from './ElementPreviewFactory';
import TrueOrFalseButton from './trueOrFalse/TrueOrFalseButton';
import ActivityHeading from './ActivityHeading';
import LoadingView from '@/pages/courses/LoadingView';
import useActivityView from '@/data/hook/useActivityView';
import { updateTrueOrFalseProgress } from '@/data/services/question/question-progress/trueOrFalseProgressServices';
import ErrorComponent from '@/components/ui/custom/ErrorComponent';
import useAuth from '@/data/hook/useAuth';
import { isStudent } from '@/functions/auth';
import {
  QuestionAttempt,
  TrueOrFalseAttemptAnwser,
} from '@/models/QuestionAttempt';
import { TrueOrFalseAnswer } from '@/models/QuestionAnswer';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import {
  trueOrFalseActivitiesQueryKeys,
  trueOrFalseActivityProgressQueryKeys,
} from '@/data/services/querykeys';
import { ActivityTypeEnum } from '@/models/_deprecated/ActivityElement';
import { useAnswerDebounce } from './ActivitiesPreview.hooks';

export default function TrueOrfalseElement(props: ElementPreviewFactoryProps) {
  const { user } = useAuth();

  const queryClient = useQueryClient();

  const { blockStepNavigate, invalidate, status } = useActivityView();

  const studentView = isStudent(user?.userType);

  const { questionId, activityMode, answer, progressId } = props;
  const { attemptElement } = props;

  const { queryFn: trueOrFalseActivityProgressFn, queryKey: progressKey } =
    trueOrFalseActivityProgressQueryKeys.get({
      trueOrFalseId: questionId,
      trueOrFalseProgressId: progressId,
    });

  const {
    data: questionProgress,
    isInitialLoading: loadingQuestionProgress,
    isError: questionProgressError,
  } = useQuery({
    queryFn: trueOrFalseActivityProgressFn,
    queryKey: progressKey,
    enabled:
      studentView && !!progressId && !!questionId && status === 'inProgress',
    cacheTime: 0,
  });

  const questionProgressErrorDetail = getErrorMessage(questionProgressError);

  const {
    data: question,
    isInitialLoading: loadingQuestion,
    error: questionError,
  } = useQuery({
    ...trueOrFalseActivitiesQueryKeys.get(questionId),
    enabled: !studentView && !!questionId,
  });

  const questionErrorDetail = getErrorMessage(questionError);

  const saveAnswer = async (answer: boolean) => {
    blockStepNavigate(true);
    const questionProgress = await updateTrueOrFalseProgress(
      { trueOrFalseId: questionId, trueOrFalseProgressId: progressId },
      {
        answer,
        isDone: true,
      },
    );

    return questionProgress;
  };

  const { mutate: updateAnswer } = useMutation(saveAnswer, {
    async onSuccess(data) {
      queryClient.setQueryData(progressKey, data);
      await invalidate(ActivityTypeEnum.TRUE_OR_FALSE, data);
    },

    onError(error: any) {
      alert.error(getErrorMessage(error));
    },

    onSettled() {
      blockStepNavigate(false);
    },
  });

  const loading = loadingQuestion || loadingQuestionProgress;

  const errorDetail = questionErrorDetail || questionProgressErrorDetail;

  if (loading) {
    return <LoadingView />;
  }

  if (errorDetail) {
    return <ErrorComponent errorTextTitle={errorDetail} />;
  }

  if (questionProgress) {
    return (
      <Element
        questionProgress={questionProgress}
        activityMode={activityMode}
        answer={answer}
        attemptElement={attemptElement}
        updateAnswer={updateAnswer}
      />
    );
  }

  if (question) {
    return (
      <Element
        question={question}
        activityMode={activityMode}
        answer={answer}
      />
    );
  }

  return <Fragment />;
}

type ElementProps = Pick<
  ElementPreviewFactoryProps,
  'activityMode' | 'answer'
> & {
  questionProgress?: TrueOrFalseActivityProgress;
  question?: TrueOrFalseActivity;
  updateAnswer?: (answer: boolean) => void;
  attemptElement?: any;
};

function Element({
  questionProgress,
  question,
  activityMode,
  updateAnswer,
  attemptElement,
  answer,
}: ElementProps) {
  if (questionProgress) {
    const { trueOrFalseActivity: question } = questionProgress;

    const selectedAnswer = (): boolean | undefined => {
      if (attemptElement && attemptElement.answers) {
        const attempt = new QuestionAttempt(attemptElement);

        const answers = new TrueOrFalseAttemptAnwser(attempt.answers);

        return answers.answer ?? undefined;
      }

      return questionProgress.answer ?? undefined;
    };

    return (
      <Content
        question={question}
        activityMode={activityMode}
        updateAnswer={updateAnswer}
        selectedAnswer={selectedAnswer()}
      />
    );
  }

  if (question) {
    const selectedAnswer = (): boolean | undefined => {
      if (answer) {
        const trueOrFalseAnswer = answer as TrueOrFalseAnswer;

        return trueOrFalseAnswer.isTrue;
      }
    };
    return (
      <Content
        question={question}
        activityMode={activityMode}
        updateAnswer={updateAnswer}
        selectedAnswer={selectedAnswer()}
      />
    );
  }

  return <Fragment />;
}

type ContentProps = Pick<ElementPreviewFactoryProps, 'activityMode'> & {
  question: TrueOrFalseActivity;
  updateAnswer?: (answer: boolean) => void;
  selectedAnswer?: boolean;
};

function Content({
  question,
  updateAnswer,
  selectedAnswer,
  activityMode,
}: ContentProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'lesson.activity.trueOrFalse',
  });

  const [selected, setSelected] = useState<boolean>();
  const { debounce } = useAnswerDebounce();

  useEffect(() => {
    setSelected(selectedAnswer);
    return () => setSelected(undefined);
  }, [selectedAnswer]);

  const updateSelectedAnswer = (answer: boolean) => {
    setSelected(answer);
    debounce(() => updateAnswer?.(answer));
  };

  return (
    <div className="flex flex-col gap-8">
      <ActivityHeading
        heading={question.question}
        subheading={t('question')}
        content={question.content}
      />
      <div className="flex flex-col w-full sm:flex-row justify-center gap-4 items-center">
        <TrueOrFalseButton
          variant={true}
          selectedValue={selected}
          onClick={() => updateSelectedAnswer(true)}
          disabled={activityMode !== 'activity'}
        />
        <TrueOrFalseButton
          variant={false}
          selectedValue={selected}
          onClick={() => updateSelectedAnswer(false)}
          disabled={activityMode !== 'activity'}
        />
      </div>
    </div>
  );
}
